/**
 * My Company Module - MyCompanyHome Page
 *
 * Página principal con pestañas para My Company
 */
import { useState, useRef, useEffect } from 'react'
import { RefreshCw, Building2, Tag, Store, Users, TrendingUp } from 'lucide-react'
import apiClient from '../../../shared/lib/api'
import MyCompanyTab from './tabs/MyCompanyTab'
import MyCompetitorsTab from './tabs/MyCompetitorsTab'
import MyBenchmarksTab from './tabs/MyBenchmarksTab'
import MyBrandsTab from './tabs/MyBrandsTab'
import MyStoresTab from './tabs/MyStoresTab'

type TabId = 'company' | 'competitors' | 'benchmarks' | 'brands' | 'stores'

const SYNC_COUNTDOWN_SECONDS = 30

const tabs: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'company', label: 'Company Profile', icon: Building2 },
  { id: 'brands', label: 'Brands', icon: Tag },
  { id: 'stores', label: 'Stores', icon: Store },
  { id: 'competitors', label: 'Competitors', icon: Users },
  { id: 'benchmarks', label: 'Benchmarks', icon: TrendingUp },
]

export default function MyCompanyHome() {
  const [activeTab, setActiveTab] = useState<TabId>('company')
  const [isSyncing, setIsSyncing] = useState(false)
  const [countdown, setCountdown] = useState<number | null>(null)
  const competitorsTabRef = useRef<{ refresh: () => void } | null>(null)
  const brandsTabRef = useRef<{ refresh: () => void } | null>(null)
  const storesTabRef = useRef<{ refresh: () => void } | null>(null)
  const companyTabRef = useRef<{ refresh: () => void } | null>(null)

  const handleRefresh = async () => {
    try {
      setIsSyncing(true)
      setCountdown(SYNC_COUNTDOWN_SECONDS)

      // 1. POST trigger-lookup to request data update
      await apiClient.post('/my-company/trigger-lookup')
    } catch (error) {
      console.error('Error triggering lookup:', error)
      setIsSyncing(false)
      setCountdown(null)
      return
    }

    // 2. Countdown is handled by useEffect below - when it reaches 0, we fetch
  }

  // Countdown effect: when countdown reaches 0, fetch company data and refresh tabs
  useEffect(() => {
    if (countdown === null || countdown > 0) return

    const fetchAfterCountdown = async () => {
      try {
        // 3. GET company - refresh all tabs
        if (companyTabRef.current) {
          companyTabRef.current.refresh()
        }
        if (competitorsTabRef.current) {
          competitorsTabRef.current.refresh()
        }
        if (brandsTabRef.current) {
          brandsTabRef.current.refresh()
        }
        if (storesTabRef.current) {
          storesTabRef.current.refresh()
        }
      } catch (error) {
        console.error('Error fetching data after sync:', error)
      } finally {
        setIsSyncing(false)
        setCountdown(null)
      }
    }

    fetchAfterCountdown()
  }, [countdown])

  // Countdown timer: decrement every second from 30 to 0
  useEffect(() => {
    if (countdown === null || countdown <= 0) return

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev === null || prev <= 1) return 0
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [countdown])

  return (
    <div className="min-h-[calc(100vh-8rem)]">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-lg">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold tracking-tight text-tf-text">
                  My Company
                </h1>
                <p className="text-sm text-tf-text-muted mt-0.5">
                  Company profile, brands, stores, and competitive intelligence
                </p>
              </div>
            </div>
          </div>
          <button
            onClick={handleRefresh}
            disabled={isSyncing}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-tf-text-sub bg-tf-bg-card border border-tf-border rounded-lg hover:bg-tf-bg-muted hover:border-tf-border-hover transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            title="Trigger lookup and refresh company data from AgentPlat"
          >
            <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
            {isSyncing && countdown !== null ? `Syncing ${countdown}s` : 'Sync data'}
          </button>
        </div>
      </div>

      {/* Tabs - Professional pill style */}
      <div className="mb-6">
        <nav className="flex gap-1 p-1 bg-tf-bg-hover rounded-xl w-fit" role="tablist">
          {tabs.map((tab) => {
            const Icon = tab.icon
            const isActive = activeTab === tab.id
            return (
              <button
                key={tab.id}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${isActive ? 'bg-tf-bg-card text-tf-text shadow-sm border border-tf-border' : 'text-tf-text-sub hover:text-tf-text hover:bg-tf-bg-muted' }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="rounded-xl border border-tf-border bg-tf-bg-card shadow-sm overflow-hidden">
        <div className="p-6 md:p-8">
          {activeTab === 'company' && <MyCompanyTab ref={companyTabRef} />}
          {activeTab === 'brands' && <MyBrandsTab ref={brandsTabRef} />}
          {activeTab === 'stores' && <MyStoresTab ref={storesTabRef} />}
          {activeTab === 'competitors' && <MyCompetitorsTab ref={competitorsTabRef} />}
          {activeTab === 'benchmarks' && <MyBenchmarksTab />}
        </div>
      </div>
    </div>
  )
}

